import { Body2, Body3, Tiny1 } from 'components/Typography';
import {
  Cell,
  HeadCell,
  HeadRow,
  Row,
  Table,
} from '@dapp/multi-lib/sdaoui/Table';
import moment from 'moment';
import { formatAddress } from '@dapp/multi-lib/lib/format-balance';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { useTranslation } from 'react-i18next';
import { VaultIdentity } from 'structure/pv2/vault/utils/vault';
import { useVaultTransactions } from 'structure/pv2/vault/hooks/useVault';
import { VaultTransaction } from '../../../../api/DynaVaultsAPI';
import { getTokenIconUrl } from 'utils/url';
import Image from 'next/image';
import { withStyle } from 'baseui';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { twMerge } from 'tailwind-merge';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import React, { useState } from 'react';
import { getTransactionMetadata } from 'structure/pv2/vault/utils/vaultTransactions';
import { TokenImage } from 'components/TokenImage';
import { getExplorerLink } from '@dapp/multi-lib/network';
import { VaultDecisionReasoningModal } from './VaultDecisionReasoningModal';

enum TxnType {
  'TRANSACTION' = 'TXN',
  'ADDRESS' = 'ADDRESS',
}

interface AddressProps {
  address: string;
  chainId: number;
  type: TxnType;
}

export const Address = (props: AddressProps) => {
  const { address, chainId, type } = props;

  const explorerUrl = getExplorerLink(chainId, address, type);

  return (
    <div className='flex flex-row items-center gap-1'>
      <Body2>{formatAddress(address)}</Body2>
      <a href={explorerUrl} target='_blank'>
        <Icon icon='open_in_new' className='text-black-600' />
      </a>
    </div>
  );
};

interface TableRowProps {
  transaction: VaultTransaction;
  onOpenModal: (transaction: VaultTransaction) => void;
  vaultDecimals: number;
}

const TableRow = (props: TableRowProps) => {
  const { transaction, onOpenModal } = props;
  const chainId = transaction.chainId;
  const { t } = useTranslation('vault');

  const dateString = moment(transaction.timestamp)
    .local()
    .format('MMM D, YYYY, h:mm A');

  const metadata = getTransactionMetadata(transaction);

  return (
    <Row>
      <Cell borderStyle='none' className='!py-[20px] !pl-[24px]'>
        <Address
          type={TxnType.TRANSACTION}
          chainId={chainId}
          address={transaction.txnHash}
        />
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        <Body2 className='lowercase first-letter:uppercase'>
          {transaction.method}
        </Body2>
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        <Body2>{dateString}</Body2>
      </Cell>
      <Cell borderStyle='none' className='!py-[20px] !pr-0'>
        <div className='ml-auto flex flex-row gap-2'>
          <Address
            type={TxnType.ADDRESS}
            chainId={chainId}
            address={transaction.sender}
          />
          <Body3 className='text-green'>IN</Body3>
        </div>
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        <Address
          type={TxnType.ADDRESS}
          chainId={chainId}
          address={transaction.recipient}
        />
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        {metadata && <Body2>{metadata.shares}</Body2>}
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        <Body2 className='lowercase first-letter:uppercase'>
          {metadata && metadata.amount}
        </Body2>
      </Cell>
      <Cell borderStyle='none' className='!py-[20px]'>
        {metadata && metadata.token && (
          <div className='flex flex-row items-center gap-1'>
            <TokenImage
              symbol={metadata.token.symbol}
              src={getTokenIconUrl(metadata.token.symbol)}
              height='24'
              width='24'
              alt={metadata.token.name}
            />
            <Body2>{metadata.token.symbol}</Body2>
          </div>
        )}
      </Cell>
      <Cell borderStyle='none' className={'cursor-pointer gap-1 !py-[20px]'}>
        {transaction.reasoning ? (
          <div className='flex gap-2' onClick={() => onOpenModal(transaction)}>
            <Image
              src='/static/svgs/open_in_new.svg'
              alt='open-in-new'
              width={14}
              height={14}
            />
            <Body2>{t('VAULT_DECISION_REASONING_TABLE.VIEW')}</Body2>
          </div>
        ) : (
          '-'
        )}
      </Cell>
    </Row>
  );
};

const MobileHeadCell = withStyle(HeadCell, {
  paddingLeft: 0,
  paddingBottom: 0,
  paddingRight: 0,
  paddingTop: 0,
  ':first-child': {
    paddingLeft: '8px',
  },
  ':last-child': {
    paddingRight: '8px',
  },
});

const MobileCell = withStyle(Cell, {
  backgroundColor: 'transparent',
  paddingTop: '6px',
  paddingBottom: '6px',
  borderLeftStyle: 'none',
  borderRightStyle: 'none',
  borderTopStyle: 'none',
  borderBottomStyle: 'none',
  ':first-child': {
    borderTopLeftRadius: 'none',
    borderBottomLeftRadius: 'none',
    borderLeftStyle: 'none',
    paddingLeft: '8px',
  },
  ':last-child': {
    borderTopRightRadius: 'none',
    borderBottomRightRadius: 'none',
    borderRightStyle: 'none',
    paddingRight: '8px',
  },
});

const TableRowMobile = (props: TableRowProps) => {
  const { transaction, onOpenModal } = props;
  const chainId = transaction.chainId;
  const { t } = useTranslation('vault');
  const [showDetails, setShowDetails] = useState(false);

  const dateString = new Date(transaction.timestamp).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );

  const metadata = getTransactionMetadata(transaction);

  return (
    <Row>
      <div
        role='grid'
        className={`col-span-4 grid grid-cols-[repeat(4,_auto)_28px] rounded-[6px] border border-solid  border-black-700 bg-black-800`}
      >
        <MobileCell>
          {metadata && metadata.token && (
            <div className='flex flex-row items-center gap-1'>
              <Image
                src={getTokenIconUrl(metadata.token.symbol)}
                alt={metadata.token.name}
                width={16}
                height={16}
                className='h-[16px] w-[16px]'
              />
              <Body2>{metadata.token.symbol}</Body2>
            </div>
          )}
        </MobileCell>
        <MobileCell>{metadata && <Body2>{metadata.amount}</Body2>}</MobileCell>
        <MobileCell>
          <Body2>{dateString}</Body2>
        </MobileCell>
        <MobileCell className='cursor-pointer'>
          {transaction.reasoning ? (
            <div
              className='flex gap-2'
              onClick={() => onOpenModal(transaction)}
            >
              <Image
                src='/static/svgs/open_in_new.svg'
                alt='open-in-new'
                width={14}
                height={14}
              />
              <Body2>{t('VAULT_DECISION_REASONING_TABLE.VIEW')}</Body2>
            </div>
          ) : (
            '-'
          )}
        </MobileCell>
        <MobileCell
          $style={{
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          <ButtonIcon
            className={twMerge(
              'self-start p-2',
              showDetails ? '-rotate-180' : 'rotate-0'
            )}
            onClick={() => setShowDetails((state) => !state)}
          >
            <Icon icon='arrow_down' color='#F0F0F0' width={12} height={12} />
          </ButtonIcon>
        </MobileCell>

        <div
          className={twMerge(
            'col-span-4 pb-2 pl-[28px] pr-2',
            showDetails ? 'block' : 'hidden'
          )}
        >
          <div className='flex flex-row items-center gap-2'>
            <Tiny1 className='text-white-400'>Tnx Hash: </Tiny1>
            <Address
              type={TxnType.TRANSACTION}
              chainId={chainId}
              address={transaction.txnHash}
            />
            <div className='h-[12px] w-[1px] bg-black-600' />
            <Tiny1 className='text-white-400'>Method: </Tiny1>
            <Body2 className='lowercase first-letter:uppercase'>
              {transaction.method}
            </Body2>
          </div>

          <div className='flex flex-row items-center gap-2'>
            <Address
              type={TxnType.ADDRESS}
              chainId={chainId}
              address={transaction.sender}
            />
            <Body3 className='text-green'>IN</Body3>
            <Address
              type={TxnType.ADDRESS}
              chainId={chainId}
              address={transaction.recipient}
            />
          </div>
        </div>
      </div>
    </Row>
  );
};

interface VaultReportingTableProps {
  vaultIdentity: VaultIdentity;
  vaultDecimals: number;
}

export const VaultReportingTable = (props: VaultReportingTableProps) => {
  const { t } = useTranslation('vault');
  const { vaultIdentity } = props;
  const isMobile = useIsMobile();

  const [vaultModal, setVaultModal] = useState(false);
  const transactionsQuery = useVaultTransactions(vaultIdentity);
  const [selectedDecision, setSelectedDecision] =
    useState<VaultTransaction | null>(null);

  const handleOpenModal = (decision: VaultTransaction) => {
    setSelectedDecision(decision);
    setVaultModal(true);
  };
  if (transactionsQuery.isLoading || !transactionsQuery.data) {
    return null;
  }

  if (isMobile) {
    return (
      <div>
        <Table role='grid' $gridTemplateColumns='repeat(3, auto)_28px'>
          <HeadRow>
            <MobileHeadCell>{t('VAULT_REPORTING_TABLE.TOKEN')}</MobileHeadCell>
            <MobileHeadCell>{t('VAULT_REPORTING_TABLE.AMOUNT')}</MobileHeadCell>
            <MobileHeadCell>{t('VAULT_REPORTING_TABLE.DATE')}</MobileHeadCell>
            <MobileHeadCell>
              {t('VAULT_DECISION_REASONING_TABLE.DETAILS')}
            </MobileHeadCell>
          </HeadRow>
          {transactionsQuery.data.map((transaction) => (
            <TableRowMobile
              key={transaction.txnHash}
              vaultDecimals={props.vaultDecimals}
              transaction={transaction}
              onOpenModal={handleOpenModal}
            />
          ))}
        </Table>
        <VaultDecisionReasoningModal
          open={vaultModal}
          onOpenChange={setVaultModal}
          decision={selectedDecision}
        />
      </div>
    );
  }

  return (
    <div>
      <Table
        role='grid'
        $gridTemplateColumns={`repeat(3, auto) repeat(2, max-content) repeat(4, auto)`}
      >
        <HeadRow>
          <HeadCell>{t('VAULT_REPORTING_TABLE.TXN_HASH')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.METHOD')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.DATE')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.FROM')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.TO')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.SHARES')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.AMOUNT')}</HeadCell>
          <HeadCell>{t('VAULT_REPORTING_TABLE.TOKEN')}</HeadCell>
          <HeadCell>{t('VAULT_DECISION_REASONING_TABLE.DETAILS')}</HeadCell>
        </HeadRow>

        {transactionsQuery.data.map((transaction) => (
          <TableRow
            key={transaction.txnHash}
            vaultDecimals={props.vaultDecimals}
            transaction={transaction}
            onOpenModal={handleOpenModal}
          />
        ))}
      </Table>
      <VaultDecisionReasoningModal
        open={vaultModal}
        onOpenChange={setVaultModal}
        decision={selectedDecision}
      />
    </div>
  );
};
